import { useRollbar } from '@rollbar/react'
import { isWebsitePageType } from 'client/store/page/pageSelectors'
import { usePage } from 'client/store'
import {
  removePopup as removePopupAction,
  setEditingEntity,
  toggleActivePopup,
} from 'client/store/management/managementActions'
import { createPopupStructure as createPopupStructureAction } from 'client/store/page/pageActions'
import pageSelectors from 'client/store/page/pageSelectors'
import { pageTypes } from 'common/constants/pageTypes'
import {
  BaseEntityInterface,
  EntityInnerItemInterface,
} from 'common/types/entities/EntityInterface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { PopupInterface } from 'common/types/entities/popup-interface'
import React from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useDispatch } from 'react-redux'
import { PlusCircleIcon } from 'src/editor/shared/icons/plus-circle-icon'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import { createPopupFromLibrary } from '../library/creators/create-popup'
import { createPopupWebinarRegistrationFromLibrary } from '../library/creators/create-popup-webinar-registration'
import { createPopupWithAgreementFromLibrary } from '../library/creators/create-popup-with-agreement'
import { PopupItem } from './popup-item'
import { createWebsitePopupFromLibrary } from '../library/creators/create-website-popup'

export const PopupManagement = () => {
  const { t } = useTypedTranslation()
  const dispatch = useDispatch()
  const rollbar = useRollbar()

  const websiteGlobalPopups = usePage(
    pageSelectors.getWebsiteGlobalPopupEntities,
  )
  const pagePopups = usePage(pageSelectors.getPagePopupEntities)
  const pageType = usePage(pageSelectors.getPageType)
  const isWebsitePage = usePage(isWebsitePageType)
  const pageLocale = usePage(pageSelectors.getPageLocale)
  const nextPopupNumber = usePage(pageSelectors.getNextPopupNumber)

  const onToggleDisplay = (id: string, showSettings?: boolean) => {
    dispatch(toggleActivePopup(id))

    if (showSettings) {
      dispatch(setEditingEntity(id))
    }
  }

  const onRemove = (id: string) => dispatch(removePopupAction(id))
  const createPopupStructure = (structure: {
    [id: string]:
      | EntityInnerItemInterface
      | BaseEntityInterface
      | PopupInterface
      | OldEntityInterface
  }) => dispatch(createPopupStructureAction(structure))

  const createPopup = () => {
    let popup: {
      ascendantId: string
      structure: {
        [id: string]:
          | EntityInnerItemInterface
          | BaseEntityInterface
          | PopupInterface
          | OldEntityInterface
      }
    } | null = null
    if (pageType === pageTypes.offerPage || pageType === pageTypes.offerInner) {
      popup = createPopupWithAgreementFromLibrary(pageLocale)
    } else if (pageType === pageTypes.webinarRegistration) {
      popup = createPopupWebinarRegistrationFromLibrary(pageLocale)
    } else if (isWebsitePage) {
      popup = createWebsitePopupFromLibrary(pageLocale)
    } else {
      popup = createPopupFromLibrary(pageLocale)
    }

    if (!popup) {
      return rollbar.error('Popup creation failed')
    }
    ;(popup.structure[popup.ascendantId] as PopupInterface).title =
      `Pop-up ${nextPopupNumber}`
    createPopupStructure(popup.structure)
    onToggleDisplay(popup.ascendantId)
  }

  const renderPopupItem = (popupEntity: PopupInterface) => (
    <PopupItem
      entity={popupEntity}
      title={popupEntity.title || ''}
      onToggleDisplay={onToggleDisplay}
      onRemove={onRemove}
      key={popupEntity.id}
      withoutWebsiteGlobal={!isWebsitePage}
    />
  )

  const renderPopupItemGroup = (title: string, popups: PopupInterface[]) => (
    <div className="flex flex-col gap-1">
      <span className="mb-3 text-M text-textTertiary">{title}</span>
      {popups.map(popup => renderPopupItem(popup))}
    </div>
  )

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <span className="text-heading-4">{t('popup_management.pop-ups')}</span>
        <GhostIconButton
          onClick={createPopup}
          icon={<PlusCircleIcon title={t('popup_management.create_page.title')} />}
          title={t('popup_management.create_page.title')}
        />
      </div>
      {websiteGlobalPopups.length === 0 && pagePopups.length === 0 ? (
        <ManagementEmpty />
      ) : (
        <div className="flex flex-col gap-5">
          {websiteGlobalPopups.length > 0 &&
            isWebsitePage &&
            renderPopupItemGroup(
              t('popup_management.website_global_type'),
              websiteGlobalPopups as PopupInterface[],
            )}
          {pagePopups.length > 0 &&
            renderPopupItemGroup(
              t('popup_management.page_type'),
              pagePopups as PopupInterface[],
            )}
        </div>
      )}
    </>
  )
}

function ManagementEmpty() {
  const { t } = useTypedTranslation()

  return (
    <div className="mt-12 text-center text-textTertiary">
      <span className="text-S">{t('popup_management.empty_popups')}</span>
    </div>
  )
}
